// Pay-period date math for BBM's bi-weekly Sat→Fri × 2 cycle.
//
// Background (Jaylen, 2026-05-12):
//   • Pay periods are 14 days long
//   • Each pay period starts on a Saturday and ends on a Friday
//   • Direct deposit processed Sunday after period close
//   • Funds hit employee accounts Tuesday
//   • Current pay period (as of writing): Sat May 2 – Fri May 15 2026
//
// The anchor below is any known pay-period start date. From there we
// compute which pay period contains any given date by counting whole
// 14-day windows since the anchor (negative = pre-anchor periods).
// This avoids storing a list of pay periods anywhere — the math is
// fully derived.
//
// Used by:
//   • Recaps page preset chips (pay-period framing matches payroll)
//   • Payroll page date filters + CSV export filenames
//   • Anywhere else we eventually surface "what period are we in"

import { format, addDays, parseISO, differenceInCalendarDays, startOfDay } from 'date-fns';

// Anchor: Sat 2026-05-02 is the start of the pay period that covers
// May 2–May 15 2026. Any date in that period yields periodIndex = 0;
// the next period (May 16–May 29) is periodIndex = 1; the previous
// (Apr 18–May 1) is periodIndex = -1. Bump this only if BBM moves to
// a different pay-period cadence (which would invalidate every prior
// CSV export filename too — not a casual change).
export const PAY_PERIOD_ANCHOR_ISO = '2026-05-02';
export const PAY_PERIOD_LENGTH_DAYS = 14;

// Compute which pay period contains the given Date. Returns:
//   { index, start: Date, end: Date, startIso: 'YYYY-MM-DD', endIso }
// where start is the Saturday at 00:00 local and end is the Friday at
// 23:59:59.999 local of that period.
export function payPeriodFor(date) {
    const anchor = startOfDay(parseISO(PAY_PERIOD_ANCHOR_ISO));
    const target = startOfDay(date);
    const daysSinceAnchor = differenceInCalendarDays(target, anchor);
    const index = Math.floor(daysSinceAnchor / PAY_PERIOD_LENGTH_DAYS);
    const start = addDays(anchor, index * PAY_PERIOD_LENGTH_DAYS);
    const end = addDays(start, PAY_PERIOD_LENGTH_DAYS - 1);
    // Push end to end-of-day for inclusive range queries.
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);
    return {
        index,
        start,
        end: endOfDay,
        startIso: format(start, 'yyyy-MM-dd'),
        endIso: format(end, 'yyyy-MM-dd'),
    };
}

// Convenience: shift a pay period by N (1 = next period, -1 = previous).
export function shiftPayPeriod(period, n) {
    const newStart = addDays(period.start, n * PAY_PERIOD_LENGTH_DAYS);
    return payPeriodFor(newStart);
}

// Human-friendly label for a pay period — used in date-range UI and
// CSV filenames. Returns "May 2 – May 15" within a year, "Dec 27 –
// Jan 9, 2027" when the period crosses a year boundary.
export function formatPayPeriod(period) {
    const sameYear = period.start.getFullYear() === period.end.getFullYear();
    if (sameYear) {
        return `${format(period.start, 'MMM d')} – ${format(period.end, 'MMM d')}`;
    }
    return `${format(period.start, 'MMM d, yyyy')} – ${format(period.end, 'MMM d, yyyy')}`;
}

// File-safe identifier for CSV exports — e.g. "2026-05-02_2026-05-15".
// Stable, sortable, no ambiguity across years.
export function payPeriodSlug(period) {
    return `${period.startIso}_${period.endIso}`;
}

// Return date strings ('YYYY-MM-DD') suitable for db.getRecaps and
// db.getCrewDayHours ({ since, until }). Both filter DATE columns
// (move_date / work_date), so plain date strings compare exactly —
// full ISO timestamps here previously coerced Fri 23:59 ET to the
// NEXT Saturday in UTC, leaking one extra day into the window (and
// QA M6 BUG-3: windowing recaps by recap_submitted_at dropped
// late-submitted recaps out of their pay period entirely).
export function payPeriodIsoRange(period) {
    return {
        since: period.startIso,
        until: period.endIso,
    };
}

// Split a pay period into its two constituent weeks (Sat→Fri × 2).
// Used by the Payroll page to compute per-week overtime — anything
// over 40h in a single week is OT regardless of what the other week
// looked like.
//
// Returns: [{ start, end, startIso, endIso, label }, { ... }]
// where start is Saturday 00:00 local and end is Friday 23:59:59.999.
export function splitPayPeriodWeeks(period) {
    const week1Start = period.start;
    const week1End = new Date(period.start);
    week1End.setDate(week1End.getDate() + 6); // first Friday
    week1End.setHours(23, 59, 59, 999);

    const week2Start = new Date(period.start);
    week2Start.setDate(week2Start.getDate() + 7); // second Saturday
    week2Start.setHours(0, 0, 0, 0);

    const week2End = period.end; // already end-of-Friday

    return [
        {
            start: week1Start,
            end: week1End,
            startIso: format(week1Start, 'yyyy-MM-dd'),
            endIso: format(week1End, 'yyyy-MM-dd'),
            label: 'Week 1',
        },
        {
            start: week2Start,
            end: week2End,
            startIso: format(week2Start, 'yyyy-MM-dd'),
            endIso: format(week2End, 'yyyy-MM-dd'),
            label: 'Week 2',
        },
    ];
}
